// =============================================================================
// src/lib/ridesCore.js - ride help (NC Medicaid rides) display rules.
// Pure: unit tested. Server side: 20261224100000_ride_help_hub
// (appointment_rides, ride_mark_booked).
//
// Ride status: requested (someone has to book it), booked, cancel_needed
// (the visit was cancelled but the vendor still has the trip), cancelled.
//
// Rides at booking (20261226100000_ride_plan_at_booking): each patient has a
// transportation profile (patient_transport) and each visit a ride plan
// (appointments.ride_plan). A ride is one of four kinds (provider_type):
// nemt (the health plan's ride, booked by the office), care_manager or chw
// (the care team drives; the driver gets a visit on their Care Schedule),
// family (no work for anyone).
// =============================================================================
import { conflictRange } from "./careConflictsCore.js";

export const RIDE_SOURCES = [
  { value: "nemt", label: "Medicaid ride (NEMT)" },
  { value: "care_manager", label: "Care manager drives" },
  { value: "chw", label: "CHW drives" },
  { value: "family", label: "Family or friend" },
];
export const RIDE_NEEDS = [
  { value: "never", label: "Does not need rides" },
  { value: "sometimes", label: "Sometimes needs a ride" },
  { value: "always", label: "Always needs a ride" },
];
// Who can drive a care team ride. One list everywhere: the database
// (_ride_valid_driver, _ride_is_driver_role), the driver pickers, the ride
// alerts card and the Care Schedule route (Oct 2026: RN, LPN and Health
// Educator were pickable but could not open the Care Schedule).
export const DRIVER_ROLES = {
  care_manager: ["Care Manager", "Supervising Care Manager", "Care Manager Supervisor", "Owner", "Manager"],
  chw: ["CHW"],
};
export const ALL_DRIVER_ROLES = Array.from(new Set([].concat(DRIVER_ROLES.care_manager, DRIVER_ROLES.chw)));
export const isCareTeamSource = (s) => s === "care_manager" || s === "chw";

export function sourceLabel(v) {
  const s = RIDE_SOURCES.find((x) => x.value === v);
  return s ? s.label : "";
}

// Short "who drives" words for chips and badges.
export function sourceShort(v) {
  if (v === "care_manager") return "care manager";
  if (v === "chw") return "CHW";
  if (v === "family") return "family";
  return "NEMT";
}

// "Always needs a ride - CHW (Marcus Johnson) - wheelchair". null = nothing to show.
export function transportSummary(profile, driverName = "") {
  if (!profile || (profile.ride_need !== "always" && profile.ride_need !== "sometimes")) return null;
  const head = profile.ride_need === "always" ? "Always needs a ride" : "Sometimes needs a ride";
  const who = sourceShort(profile.usual_source) + (isCareTeamSource(profile.usual_source) && driverName ? " (" + driverName + ")" : "");
  const needs = rideNeedsList(profile);
  return [head, who].concat(needs).join(" - ");
}

// ---- Ride needs (Rides block 5) ---------------------------------------------
// How the patient moves, and the extras a driver or vendor has to know.
// Server: 20270118100000_ride_block5 (patient_transport_set_needs,
// _ride_needs_text, _ride_care_team_ok).
export const MOBILITY = [
  { value: "walks", label: "Walks (no wheelchair)" },
  { value: "own_wheelchair", label: "Wheelchair, can transfer to a car seat" },
  { value: "lift_van", label: "Stays in the wheelchair (lift van)" },
  { value: "stretcher", label: "Stretcher" },
];
export const RIDE_EXTRAS = [
  { key: "oxygen", label: "Brings oxygen", short: "oxygen" },
  { key: "service_animal", label: "Service animal", short: "service animal" },
  { key: "car_seat", label: "Child car seat", short: "child car seat" },
  { key: "bariatric", label: "Bariatric seat or lift", short: "bariatric seat or lift" },
  { key: "door_to_door", label: "Door-to-door help", short: "door-to-door help" },
];
export function mobilityOf(p) {
  if (!p) return "walks";
  if (p.mobility && MOBILITY.some((m) => m.value === p.mobility)) return p.mobility;
  return p.wheelchair ? "own_wheelchair" : "walks";
}
export function mobilityShort(v) {
  return { own_wheelchair: "wheelchair", lift_van: "wheelchair van with a lift", stretcher: "stretcher" }[v] || null;
}
// A care team car can't carry a lift van or stretcher patient.
export function careTeamOk(p) {
  const m = mobilityOf(p);
  return m !== "lift_van" && m !== "stretcher";
}
export function careTeamBlockText(p) {
  if (careTeamOk(p)) return null;
  return "Needs a " + mobilityShort(mobilityOf(p)) + ": a care team car can't carry them, so book a Medicaid ride.";
}
const isEnglish = (l) => ["english", "en", "eng", "en-us"].includes(String(l || "").trim().toLowerCase());
// The interpreter language: the profile's choice ("none" = not needed), else
// the patient's preferred language when it is not English. null = none.
export function interpreterOf(p, preferredLanguage = "") {
  const v = p && p.interpreter != null ? String(p.interpreter).trim() : "";
  if (v === "none") return null;
  if (v) return v;
  const pl = String(preferredLanguage || "").trim();
  return pl && !isEnglish(pl) ? pl : null;
}
// What to save for the interpreter: on = the language (or the preferred one),
// off = "none" when the patient's language would otherwise turn it on.
export function interpreterValue(on, language, preferredLanguage = "") {
  const pl = String(preferredLanguage || "").trim();
  if (on) return String(language || "").trim() || (pl && !isEnglish(pl) ? pl : "") || null;
  return pl && !isEnglish(pl) ? "none" : null;
}
// ["wheelchair van with a lift", "oxygen", "interpreter (Spanish)", ...]
export function rideNeedsList(p, preferredLanguage = "") {
  if (!p) return [];
  const out = [mobilityShort(mobilityOf(p))];
  for (const x of RIDE_EXTRAS) {
    if (!p[x.key]) continue;
    out.push(x.key === "car_seat" && p.car_seat_age != null && p.car_seat_age !== "" ? "child car seat (age " + p.car_seat_age + ")" : x.short);
  }
  if (p.escort) out.push("rides with someone");
  const lang = interpreterOf(p, preferredLanguage);
  if (lang) out.push("interpreter (" + lang + ")");
  return out.filter(Boolean);
}
export function needsPayload(f) {
  return {
    mobility: mobilityOf(f), oxygen: !!f.oxygen, service_animal: !!f.service_animal, car_seat: !!f.car_seat,
    car_seat_age: f.car_seat && f.car_seat_age !== "" && f.car_seat_age != null ? Number(f.car_seat_age) : null,
    bariatric: !!f.bariatric, door_to_door: !!f.door_to_door, interpreter: f.interpreter == null ? null : f.interpreter,
  };
}

// Vendor pickup window: "Pickup 8:15-8:45 AM". The end is set when the ride is
// booked (pickup + the vendor's default window).
export function windowEndIso(pickupIso, minutes) {
  const n = Number(minutes);
  if (!pickupIso || !Number.isFinite(n) || n <= 0) return null;
  const t = new Date(pickupIso).getTime();
  return Number.isFinite(t) ? new Date(t + n * 60000).toISOString() : null;
}
export function windowMinutes(pickupIso, endIso) {
  if (!pickupIso || !endIso) return null;
  const n = Math.round((new Date(endIso).getTime() - new Date(pickupIso).getTime()) / 60000);
  return Number.isFinite(n) && n > 0 ? n : null;
}
export function windowLabel(pickupIso, endIso) {
  if (!pickupIso) return null;
  if (!endIso || new Date(endIso).getTime() <= new Date(pickupIso).getTime()) return "Pickup " + pickupTime(pickupIso);
  return "Pickup " + pickupTime(pickupIso) + "-" + pickupTime(endIso);
}

// "HH:MM" for a 15-minute slot minus the lead time (pickup before the visit).
export function defaultPickupHHMM(startSlot, leadMinutes = 45) {
  const n = Number(startSlot);
  if (!Number.isFinite(n)) return "";
  const m = Math.max(0, n * 15 - (Number(leadMinutes) || 45));
  const p = (x) => String(x).padStart(2, "0");
  return p(Math.floor(m / 60)) + ":" + p(m % 60);
}
// ---- Practice time (Oct 2026) ----------------------------------------------
// Ride times are shown and entered in the practice's time zone, not the
// browser's (a staff member traveling, or a laptop set to another zone).
// The staff shell calls setRideTimeZone(practices.timezone); unset = browser.
let RIDE_TZ = null;
export function setRideTimeZone(tz) {
  try { if (tz) new Intl.DateTimeFormat("en-US", { timeZone: tz }); RIDE_TZ = tz || null; } catch (e) { RIDE_TZ = null; }
}
export function rideTimeZone() { return RIDE_TZ; }
// Wall clock parts of an instant in the ride zone.
function wallParts(d) {
  if (!RIDE_TZ) return { y: d.getFullYear(), mo: d.getMonth() + 1, da: d.getDate(), h: d.getHours(), mi: d.getMinutes() };
  const o = {};
  new Intl.DateTimeFormat("en-US", { timeZone: RIDE_TZ, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })
    .formatToParts(d).forEach((x) => { o[x.type] = x.value; });
  return { y: Number(o.year), mo: Number(o.month), da: Number(o.day), h: Number(o.hour) % 24, mi: Number(o.minute) };
}
// Wall clock (YYYY-MM-DD, HH:MM) in the ride zone -> Date.
function fromWall(ymd, hhmm) {
  const [y, mo, da] = String(ymd).split("-").map(Number);
  const [h, mi] = String(hhmm).split(":").map(Number);
  if (![y, mo, da, h, mi].every(Number.isFinite)) return null;
  if (!RIDE_TZ) { const d = new Date(y, mo - 1, da, h, mi); return Number.isNaN(d.getTime()) ? null : d; }
  const guess = Date.UTC(y, mo - 1, da, h, mi);
  const off = (t) => { const w = wallParts(new Date(t)); return Date.UTC(w.y, w.mo - 1, w.da, w.h, w.mi) - t; };
  let t = guess - off(guess);
  t = guess - off(t);
  return new Date(t);
}
const pad2 = (x) => String(x).padStart(2, "0");
// Today + n days as YYYY-MM-DD in the ride zone.
export function rideYmd(offsetDays = 0, now = new Date()) {
  const w = wallParts(now);
  const d = new Date(Date.UTC(w.y, w.mo - 1, w.da + offsetDays));
  return d.toISOString().slice(0, 10);
}

export function hhmmFromIso(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const w = wallParts(d);
  return pad2(w.h) + ":" + pad2(w.mi);
}
// Visit date (YYYY-MM-DD) + "HH:MM" in the practice's zone -> ISO.
export function pickupIsoFrom(dateYmd, hhmm) {
  if (!dateYmd || !/^\d{2}:\d{2}$/.test(String(hhmm || ""))) return null;
  const d = fromWall(dateYmd, hhmm);
  return !d || Number.isNaN(d.getTime()) ? null : d.toISOString();
}

// What the booking screen saves in appointments.ride_plan. undefined = leave
// the column alone (the profile decides on a new visit; an edited visit
// keeps its ride and the ride follows the visit).
//   create: section on -> the plan; off for a patient who always needs a
//           ride -> {source: "none"} ("Not this time"); otherwise nothing
//   edit:   only when the ride section was changed
export function buildRidePlan({ isEdit = false, touched = false, on, source, driverId = "", pickupAt = null, need = "never", telehealth = false }) {
  if (telehealth) return undefined;
  if (isEdit && !touched) return undefined;
  if (!on) return (isEdit || need === "always") ? { source: "none" } : undefined;
  const plan = { source: source || "nemt" };
  if (isCareTeamSource(plan.source) && driverId) plan.driver_id = driverId;
  if (plan.source !== "family" && pickupAt) plan.pickup_at = pickupAt;
  return plan;
}

// Time only, in the browser's zone ("9:15 AM").
export function pickupTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-US", RIDE_TZ ? { hour: "numeric", minute: "2-digit", timeZone: RIDE_TZ } : { hour: "numeric", minute: "2-digit" });
}

// Day and time, in the practice zone ("Mon, Oct 19, 7:45 AM").
export function rideWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const o = { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" };
  return d.toLocaleString("en-US", RIDE_TZ ? { ...o, timeZone: RIDE_TZ } : o);
}

// A care team ride waiting for the driver to accept it
// (20270110100000_ride_driver_accept).
export function driverToAccept(ride) {
  return !!ride && ride.status === "booked" && isCareTeamSource(ride.provider_type) && ride.driver_ack === "pending" && !ride.picked_up_at;
}

// What the driver can do instead of driving (ride_driver_respond).
export const DRIVER_HANDOFFS = [
  { value: "hand_to", label: "Ask another driver" },
  { value: "nemt_self", label: "I'll book a Medicaid ride" },
  { value: "front_desk", label: "Send to the front desk" },
];
export function respondDoneText(action, driverName = "") {
  if (action === "accept") return "Ride accepted.";
  if (action === "home_accept") return "Ride home accepted. Tap Dropped off when the patient is home.";
  if (action === "home_to") return "Sent to " + (driverName || "the other driver") + ". They take the patient home once they accept.";
  if (action === "home_nemt") return "The front desk is booking a Medicaid ride home. Add your miles for the trip there.";
  if (action === "hand_to") return "Sent to " + (driverName || "the other driver") + ". They need to accept it.";
  if (action === "nemt_self") return "It is a Medicaid ride request now. The Ride help task is on your Tasks list to book it.";
  if (action === "front_desk") return "Sent to the front desk to book a Medicaid ride.";
  return "Saved.";
}

// The ride home handed on after pickup (20270111100000_ride_home_safety).
export const HOME_HANDOFFS = [
  { value: "home_to", label: "Ask another driver" },
  { value: "home_nemt", label: "Book a Medicaid ride home" },
];

// Why no ride home is needed (ride_home_not_needed).
export const HOME_NOT_NEEDED_REASONS = [
  { value: "hospital", label: "Sent to the hospital" },
  { value: "admitted", label: "Admitted" },
  { value: "family", label: "Family or a friend is taking them" },
  { value: "own", label: "Leaving on their own" },
];
export function homeNotNeededLabel(reason) {
  const r = HOME_NOT_NEEDED_REASONS.find((x) => x.value === reason);
  return r ? r.label : "Not needed";
}

// A care team ride whose ride home still belongs to the care team and can be
// handed on or called off: picked up, not home yet.
export function homeOpen(ride) {
  return !!ride && ride.status === "booked" && isCareTeamSource(ride.provider_type) && !!ride.picked_up_at
    && !ride.dropped_off_at && !ride.home_source && ride.return_mode !== "none";
}

// Badge for a visit's ride. null = nothing to show.
export function rideBadge(ride) {
  if (!ride) return null;
  if (ride.issue && ride.status !== "cancelled") return { label: "Ride did not come", variant: "red" };
  if (ride.status === "booked" && ride.provider_type === "family") return { label: "Ride: family or friend", variant: "green" };
  if (ride.status === "booked" && isCareTeamSource(ride.provider_type)) {
    const t = pickupTime(ride.pickup_at);
    return { label: "Ride: " + sourceShort(ride.provider_type) + " drives" + (t ? " - pickup " + t : ""), variant: "green" };
  }
  if (ride.status === "booked") {
    const t = pickupTime(ride.pickup_at);
    return { label: t ? "Ride booked - pickup " + t : "Ride booked", variant: "green" };
  }
  if (ride.status === "requested") return { label: "Ride requested", variant: "amber" };
  if (ride.status === "cancel_needed") return { label: "Cancel the booked ride", variant: "red" };
  return null;
}

// What the task panel offers for this ride.
//   book   - the Ride booked form (requested, or booked: edit the details)
//   cancel - the vendor still has the trip: call and cancel, then Complete
//   none   - closed
export function rideAction(ride) {
  if (!ride) return "none";
  if (ride.status === "requested" || ride.status === "booked") return "book";
  if (ride.status === "cancel_needed") return "cancel";
  return "none";
}

// <input type="datetime-local"> value <-> ISO. The visit date seeds the
// pickup time so staff only change the clock.
export function localInputFromIso(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const w = wallParts(d);
  return w.y + "-" + pad2(w.mo) + "-" + pad2(w.da) + "T" + pad2(w.h) + ":" + pad2(w.mi);
}
export function isoFromLocalInput(v) {
  if (!v) return null;
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(String(v));
  const d = m ? fromWall(m[1], m[2]) : new Date(v);
  return !d || Number.isNaN(d.getTime()) ? null : d.toISOString();
}

// #22 A scheduled ride home has to be after the visit starts (ride_set_return
// refuses it with return_before_visit). The default return pickup is an hour
// after the visit starts, so the form never starts on a time it would refuse.
export function returnTooEarly(returnIso, visitStartIso) {
  const r = returnIso ? new Date(returnIso).getTime() : NaN;
  const s = visitStartIso ? new Date(visitStartIso).getTime() : NaN;
  return Number.isFinite(r) && Number.isFinite(s) && r <= s;
}
export function defaultReturnIso(visitStartIso) {
  const s = visitStartIso ? new Date(visitStartIso).getTime() : NaN;
  return Number.isFinite(s) ? new Date(s + 60 * 60000).toISOString() : null;
}

// Readable errors from ride_mark_booked.
export function rideErrorText(e) {
  const m = String((e && (e.message || e)) || "");
  if (m.includes("not_your_task")) return "This ride is in another person's or team's queue.";
  if (m.includes("ride_not_open")) return "This ride is already closed.";
  if (m.includes("bad_window")) return "The pickup window has to end within 3 hours after the pickup time.";
  if (m.includes("too_long")) return "The trip number or vendor is too long.";
  if (m.includes("not_a_ride")) return "This task is not linked to a ride.";
  if (m.includes("bad_driver")) return "That person can't be the driver for this kind of ride.";
  if (m.includes("not_your_ride")) return "Only the driver or a manager can change this ride.";
  if (m.includes("not_authorized")) return "Your role can't change rides.";
  if (m.includes("return_time_needed")) return "Pick the return pickup time.";
  if (m.includes("return_before_visit")) return "The ride home has to be after the visit starts. Pick a later return pickup time.";
  if (m.includes("confirm_window")) return "Rides can be confirmed from the day of the visit back to the business day before.";
  if (m.includes("not_a_driver")) return "This visit is a ride. Pick a CHW or a care manager to drive, or use Can't make it to hand it back.";
  if (m.includes("pickup_after_visit")) return "The pickup has to be before the clinic visit starts.";
  if (m.includes("already_picked_up")) return "The patient was already picked up. Use Undo first.";
  if (m.includes("visit_not_under_way")) return "A ride home only is for a patient who is checked in today.";
  if (m.includes("ride_exists")) return "This visit already has a ride. Open it on the board.";
  if (m.includes("not_at_clinic_yet")) return "Tap At the clinic first.";
  if (m.includes("no_ride_home")) return "This ride has no ride home with the care team.";
  if (m.includes("ride_home_handed_on")) return "Another driver is taking the patient home.";
  if (m.includes("already_home")) return "The patient is already home.";
  if (m.includes("visit_not_open")) return "This visit is checked in, finished or cancelled, so a ride can't be set up for it.";
  if (m.includes("visit_over")) return "This visit started more than 3 hours ago.";
  if (m.includes("telehealth")) return "This is a telehealth visit, so it doesn't need a ride.";
  if (m.includes("ride_not_created")) return "The ride could not be set up. Check the visit and try again.";
  if (m.includes("duplicate_name")) return "There is already a vendor with that name.";
  if (m.includes("not_yet")) return "You can record how the ride went on the visit day or after.";
  if (m.includes("bad_miles")) return "Miles must be between 0 and 500.";
  if (m.includes("standing_incomplete")) return "A standing order needs the vendor and the date it ends.";
  if (m.includes("not_same_day")) return "Only a later visit the same day can ride on an earlier visit's trip.";
  if (m.includes("bad_value")) return "One of the values is not allowed. Check it and try again.";
  if (m.includes("not_open")) return "This visit is already closed.";
  if (m.includes("no_ride")) return "This visit has no ride.";
  return m || "Could not save the ride.";
}

// Marker on a calendar block. RIDE = a ride is set up (or being booked);
// NO RIDE = the patient always needs one and this visit has none.
// null = nothing to show (telehealth, cancelled visits, no ride needs).
export function rideMark(appt, ride, alwaysNeedsRide) {
  if (!appt || !["Scheduled", "Confirmed"].includes(appt.status)) return null;
  const active = ride && ["requested", "booked", "cancel_needed"].includes(ride.status) ? ride : null;
  if (active) {
    const b = rideBadge(active);
    return { text: "RIDE", tone: active.status === "booked" ? "ok" : "warn", title: b ? b.label : "Ride" };
  }
  if (alwaysNeedsRide && !/(tele|video|virtual)/i.test(String(appt.appt_type || ""))) {
    return { text: "NO RIDE", tone: "bad", title: "Always needs a ride - none set up for this visit" };
  }
  return null;
}

// The ride home, in words ("Return: will call", "Return: pickup 11:30 AM").
// A care team ride home: the driver brings the patient home (the default), or
// the patient gets home another way.
export const CARE_RETURN_MODES = [
  { value: "driver", label: "The driver brings them home" },
  { value: "none", label: "They get home another way" },
];
export const RETURN_MODES = [
  { value: "will_call", label: "Will call when ready" },
  { value: "scheduled", label: "Return pickup at a set time" },
  { value: "none", label: "No return ride" },
];
export function returnLabel(ride) {
  if (!ride) return "";
  if (ride.provider_type === "care_manager" || ride.provider_type === "chw") {
    if (ride.home_not_needed_reason) return "No ride home: " + homeNotNeededLabel(ride.home_not_needed_reason).toLowerCase();
    if (ride.home_source === "nemt") return ride.return_called_at ? "Return: Medicaid, called" : "Return: Medicaid, to book";
    if (ride.return_mode === "none") return "Return: gets home another way";
    if (ride.home_driver_id) return "Return: " + (ride.home_driver_name || "another driver") + (ride.home_ack === "pending" ? " (to accept)" : "");
    return "Return: the driver";
  }
  if (ride.home_not_needed_reason) return "No ride home: " + homeNotNeededLabel(ride.home_not_needed_reason).toLowerCase();
  if (ride.provider_type === "family") return "";
  if (ride.return_mode === "scheduled" && ride.return_at) return "Return: pickup " + pickupTime(ride.return_at);
  if (ride.return_mode === "none") return "No return ride";
  if (ride.return_called_at) return "Return: called";
  return "Return: will call";
}

// Which ride task this is: the booking, the ride home, or the ride that did not come.
export function rideTaskKind(ride, taskId) {
  if (!ride || !taskId) return "booking";
  if (ride.issue_task_id === taskId) return "issue";
  // A ride home only: one task books it (task_id and return_task_id match).
  if (ride.task_id === taskId) return "booking";
  if (ride.return_task_id === taskId) return "return";
  return "booking";
}

// One row on the Rides board.
// Block 2 (#18): a care team ride 15 minutes past pickup with no Picked up
// (ride_ack_sweep opens the Urgent task; the board flags it at once).
export const NO_PICKUP_MINUTES = 15;
export function noPickup(row, nowMs = Date.now()) {
  if (!row || row.status !== "booked" || !["care_manager", "chw"].includes(row.provider_type)) return false;
  if (row.picked_up_at || row.closed_reason || !row.pickup_at) return false;
  if (!["Scheduled", "Confirmed"].includes(row.visit_status || "Scheduled")) return false;
  const late = nowMs - new Date(row.pickup_at).getTime();
  return late >= NO_PICKUP_MINUTES * 60000 && late < 3 * 3600000;
}

// Block 2 (#14): "busy 9:30 AM - 10:30 AM" next to a driver in Change driver.
// conflicts come from care_schedule_conflicts (staff side only).
export const BUSY_WINDOW_MINUTES = 90;
export function busyLabel(conflicts) {
  const c = (conflicts || []).filter((x) => x && x.who === "staff");
  if (!c.length) return "";
  return "busy " + conflictRange(c[0]) + (c.length > 1 ? " (+" + (c.length - 1) + ")" : "");
}

// Block 3 (20270116100000_ride_block3): the latest NCTracks result for the
// visit month on a Medicaid ride. Flag only: booking is still allowed.
export const ELIG_STALE_DAYS = 30;
export function medicaidInactive(row) {
  return !!row && row.provider_type === "nemt" && (row.status === "requested" || row.status === "booked") && row.elig_status === "Inactive";
}
export function eligLine(row, nowMs = Date.now()) {
  if (!row || row.provider_type !== "nemt" || !(row.status === "requested" || row.status === "booked")) return null;
  const at = row.elig_checked_at ? new Date(row.elig_checked_at) : null;
  const when = at ? at.toLocaleDateString("en-US", { month: "short", day: "numeric" }) : null;
  const stale = !at || nowMs - at.getTime() > ELIG_STALE_DAYS * 86400000;
  if (!at || !row.elig_status) return { tone: "warn", text: "Medicaid not checked for this month", stale: true };
  if (row.elig_status === "Inactive") return { tone: "bad", text: "Medicaid inactive (checked " + when + "): a Medicaid ride may not be covered. Confirm coverage or switch to a care team or family ride.", stale };
  if (row.elig_status === "Verified") return { tone: "ok", text: "Medicaid active (checked " + when + ")", stale };
  return { tone: "warn", text: "Medicaid not verified (checked " + when + ")", stale: true };
}

export function boardRowView(row, nowMs = Date.now()) {
  if (!row) return null;
  if (row.missing) return { tone: "bad", status: "No ride set up" };
  if (row.closed_reason === "patient_not_home") return { tone: "bad", status: "Patient not home" };
  if (row.driver_out && row.status === "booked") return { tone: "bad", status: "Driver out: reassign" };
  if (noPickup(row, nowMs)) return { tone: "bad", status: "Not picked up: call the driver" };
  if (medicaidInactive(row)) return { tone: "bad", status: "Medicaid inactive" };
  if (driverToAccept(row)) return { tone: "warn", status: "Driver to accept" };
  if (row.status === "booked" && !row.dropped_off_at && row.home_ack === "pending") return { tone: "warn", status: "Ride home: driver to accept" };
  if (row.status === "booked" && row.home_source === "nemt" && !row.return_called_at && row.return_mode !== "none") return { tone: "warn", status: "Ride home to book" };
  if (row.closed_reason === "patient_cancelled") return { tone: "bad", status: "Patient cancelled the ride" };
  if (row.visit_status === "Cancelled" && row.cancelled_reason === "Ride did not come") return { tone: "bad", status: "Visit missed: ride did not come" };
  if (row.issue) return { tone: "bad", status: row.issue === "late" ? "Ride late (patient)" : "Ride did not come (patient)" };
  if (row.status === "requested" && row.request_reason === RETURN_ONLY_REASON) return { tone: "warn", status: "Ride home to book" };
  if (row.status === "requested") return { tone: "warn", status: "To book" };
  if (row.status === "cancel_needed") return { tone: "warn", status: "Cancel with vendor" };
  if (row.outcome === "did_not_come") return { tone: "bad", status: "Ride did not come" };
  if (row.outcome === "late") return { tone: "warn", status: row.minutes_late ? "Late " + row.minutes_late + " min" : "Ride late" };
  if (row.outcome === "patient_not_ready") return { tone: "warn", status: "Patient not ready" };
  if (row.dropped_off_at) return { tone: "ok", status: "Home" };
  if (row.outcome === "on_time") return { tone: "ok", status: "On time" };
  if (row.status === "booked") return { tone: "ok", status: "Booked" };
  return { tone: "warn", status: row.status || "" };
}

// ---- Ride tracking and insights (20261230100000_ride_insights) ---------------
export const RIDE_OUTCOMES = [
  { value: "on_time", label: "On time" },
  { value: "late", label: "Late" },
  { value: "did_not_come", label: "Didn't come" },
  { value: "patient_not_ready", label: "Not ready" },
];

// ride_return_only marks a ride home requested mid-visit with this reason.
export const RETURN_ONLY_REASON = "Ride home only.";

// Visit statuses that mean the patient is at the clinic now: "Add a ride"
// offers these as "ride home only" (ride_return_only).
export const UNDER_WAY_STATUSES = ["Checked In", "Roomed", "In Progress"];
export function isUnderWay(visitStatus) {
  return UNDER_WAY_STATUSES.includes(visitStatus || "");
}

// A care team driver taps Dropped off once the patient is home. Shown after
// At the clinic, only when the driver is also taking them home.
export function canDropOff(ride, asHomeDriver = false) {
  if (!ride || ride.status !== "booked" || !(ride.arrived_at || ride.picked_up_at) || ride.dropped_off_at) return false;
  if (!["care_manager", "chw"].includes(ride.provider_type)) return false;
  if (ride.home_source) return false;
  // Handed to another driver: only they drop the patient off.
  if (ride.home_driver_id && !asHomeDriver) return false;
  if (!ride.arrived_at && !asHomeDriver && !ride.visit_ended) return false;
  return (ride.return_mode || "driver") === "driver";
}

// The board asks how the ride went once the visit day has come, for a booked
// ride someone outside the practice drives (Medicaid ride) or a care team ride
// the driver did not track. Family rides are not asked about.
// dayYmd is the board's day (the visit date in practice time).
export function needsOutcome(row, dayYmd, todayYmd) {
  if (!row || row.missing || row.status !== "booked" || row.provider_type === "family") return false;
  return !!dayYmd && !!todayYmd && dayYmd <= todayYmd;
}

// Driver line on the board: "Picked up 9:12 AM - At the clinic 9:41 AM - 12.3 mi".
export function trackLine(row) {
  if (!row) return "";
  return [row.picked_up_at ? "Picked up " + pickupTime(row.picked_up_at) : null,
    row.arrived_at ? "At the clinic " + pickupTime(row.arrived_at) : null,
    row.dropped_off_at ? "Dropped off " + pickupTime(row.dropped_off_at) : null,
    row.miles != null && row.miles !== "" ? Number(row.miles) + " mi" : null].filter(Boolean).join(" - ");
}

const pctOf = (n, d) => (Number(d) > 0 ? Math.round((Number(n || 0) / Number(d)) * 100) : null);

// Shapes ride_insights for the Reports tab.
export function rideInsightsView(d) {
  const t = (d && d.totals) || {};
  const rated = Number(t.on_time || 0) + Number(t.late || 0) + Number(t.did_not_come || 0);
  const kept = Number(t.kept || 0);
  const missed = Number(t.missed_ride || 0) + Number(t.no_show || 0);
  return {
    rides: Number(t.rides || 0),
    byType: [
      { key: "nemt", label: "Medicaid ride", value: Number(t.nemt || 0) },
      { key: "care_manager", label: "Care manager", value: Number(t.care_manager || 0) },
      { key: "chw", label: "CHW", value: Number(t.chw || 0) },
      { key: "family", label: "Family or friend", value: Number(t.family || 0) },
    ],
    kept,
    missed,
    keptPct: pctOf(kept, kept + missed),
    onTimePct: pctOf(t.on_time, rated),
    rated,
    notRecorded: Number(t.not_recorded || 0),
    bookedOnTimePct: pctOf(t.nemt_booked_on_time, t.nemt_booked_checked),
    returnWait: t.return_wait_minutes == null ? null : Number(t.return_wait_minutes),
    returnCalls: Number(t.return_calls || 0),
    driverMiles: Number(t.driver_miles || 0),
    driverHours: Math.round((Number(t.driver_minutes || 0) / 60) * 10) / 10,
    vendors: ((d && d.vendors) || []).map((v) => ({ ...v,
      problemPct: pctOf(Number(v.late || 0) + Number(v.did_not_come || 0), Number(v.on_time || 0) + Number(v.late || 0) + Number(v.did_not_come || 0)) })),
    drivers: ((d && d.drivers) || []).map((v) => ({ ...v, hours: Math.round((Number(v.minutes || 0) / 60) * 10) / 10,
      onTimePct: pctOf(v.on_time, Number(v.on_time || 0) + Number(v.late || 0)) })),
    repeat: (d && d.repeat) || [],
    weeks: (d && d.weeks) || [],
    money: !!(d && d.money),
    keptValueCents: d && d.kept_value_cents != null ? Number(d.kept_value_cents) : null,
    avgVisitCents: d && d.avg_visit_cents != null ? Number(d.avg_visit_cents) : null,
  };
}

// ---- Round 2 ----

// Day-before confirm: a booked Medicaid ride, the day before or the day of the
// visit, not yet confirmed since it was booked.
// nextYmd: the next business day (Friday -> Monday), see nextBusinessYmd.
export function needsConfirm(row, dayYmd, todayYmd, nextYmd) {
  if (!row || row.missing || row.status !== "booked" || row.provider_type !== "nemt") return false;
  if (row.confirm_status === "confirmed" || row.confirm_status === "rebooked") return false;
  return !!dayYmd && !!todayYmd && dayYmd >= todayYmd && dayYmd <= (nextYmd || todayYmd);
}
// The next weekday after a YYYY-MM-DD (matches _ride_next_bday).
export function nextBusinessYmd(ymd) {
  const d = new Date(String(ymd) + "T12:00:00Z");
  if (Number.isNaN(d.getTime())) return null;
  do { d.setUTCDate(d.getUTCDate() + 1); } while (d.getUTCDay() === 0 || d.getUTCDay() === 6);
  return d.toISOString().slice(0, 10);
}
export function confirmLabel(row) {
  if (!row) return null;
  if (row.confirm_status === "confirmed") return "Confirmed with vendor";
  if (row.confirm_status === "rebooked") return "Rebooked with vendor";
  return null;
}

// tel: link for a vendor phone ("(866) 555-0100" -> "tel:8665550100").
export function telHref(phone) {
  const d = String(phone || "").replace(/[^0-9+]/g, "");
  return d.length >= 7 ? "tel:" + d : null;
}

// Mileage log rows for the CSV (reimbursement). No patient names; one row per
// ride, then one total row per driver.
export function driverLogRows(rows) {
  const t = (iso) => (iso ? pickupTime(iso) : "");
  const out = [];
  const totals = new Map();
  (rows || []).forEach((r) => {
    out.push({ driver: r.driver || "", role: r.driver_role || "", visit_date: r.visit_date || "", leg: r.leg || "Ride", picked_up: t(r.picked_up_at), at_clinic: t(r.arrived_at),
      minutes: r.minutes == null ? "" : Number(r.minutes), miles: r.miles == null ? "" : Number(r.miles) });
    const k = r.driver || "";
    const cur = totals.get(k) || { miles: 0, minutes: 0, rides: 0, role: r.driver_role || "" };
    cur.miles += Number(r.miles || 0); cur.minutes += Number(r.minutes || 0); cur.rides += 1;
    totals.set(k, cur);
  });
  [...totals.entries()].sort((a, b) => a[0].localeCompare(b[0])).forEach(([k, v]) => {
    out.push({ driver: "Total: " + k, role: v.role, visit_date: v.rides + " rides", leg: "", picked_up: "", at_clinic: "",
      minutes: v.minutes, miles: Math.round(v.miles * 10) / 10 });
  });
  return out;
}

// ---- Rides board cards (Oct 2026: one question at a time, Needs action first) ----
// Statuses that mean the visit is under way or over: the ride has happened.
const VISIT_UNDER_WAY = ["Checked In", "Roomed", "In Progress", "Completed", "No Show"];

// The one question a card asks, or null.
//   "outcome" - How did the ride go? Once the pickup time has passed (or the
//               visit is under way, or the day is over). Stays once answered so
//               the answer can be changed.
//   "confirm" - Confirm with the vendor, before the pickup.
// nowMs lets tests pin the clock.
export function boardStep(row, dayYmd, todayYmd, tomorrowYmd, nowMs = Date.now()) {
  if (!row || row.missing) return null;
  if (needsOutcome(row, dayYmd, todayYmd)) {
    if (row.outcome) return "outcome";
    const pickup = row.pickup_at ? new Date(row.pickup_at).getTime() : NaN;
    const started = dayYmd < todayYmd || VISIT_UNDER_WAY.includes(row.visit_status) || (!Number.isNaN(pickup) && pickup <= nowMs);
    if (started) return "outcome";
  }
  if (needsConfirm(row, dayYmd, todayYmd, tomorrowYmd)) return "confirm";
  return null;
}

// Which list a card sits in:
//   action - someone has to do something (To book, Cancel with vendor, not
//            confirmed, no ride set up, the ride did not come, How did it go?
//            not answered yet)
//   done   - the ride is over and recorded (an outcome, or the visit is done)
//   track  - booked and on track
export function boardGroup(row, dayYmd, todayYmd, tomorrowYmd, nowMs = Date.now()) {
  if (!row) return "track";
  const v = boardRowView(row, nowMs);
  if (row.missing || row.status === "requested" || row.status === "cancel_needed" || (v && v.tone === "bad") || driverToAccept(row)
      || (v && (v.status === "Ride home: driver to accept" || v.status === "Ride home to book"))) return "action";
  const step = boardStep(row, dayYmd, todayYmd, tomorrowYmd, nowMs);
  if (step === "confirm") return "action";
  if (step === "outcome" && !row.outcome) return "action";
  if (row.outcome || row.visit_status === "Completed") return "done";
  return "track";
}

// The colored edge on a card. A ride waiting on the vendor confirm, or on How
// did the ride go?, reads amber.
export function boardStripe(row, step) {
  const v = boardRowView(row);
  if (!v) return "warn";
  if (v.tone === "ok" && (step === "confirm" || (step === "outcome" && !row.outcome))) return "warn";
  return v.tone;
}

// The status pill: Booked becomes "Booked, not confirmed" while the confirm is open.
export function boardStatus(row, step) {
  const v = boardRowView(row);
  if (!v) return "";
  return v.status === "Booked" && step === "confirm" ? "Booked, not confirmed" : v.status;
}

// Filter chips over the board.
export const BOARD_FILTERS = [
  { value: "all", label: "All" },
  { value: "action", label: "Needs action" },
  { value: "nemt", label: "Medicaid" },
  { value: "care_team", label: "Care team" },
];
export function boardFilterMatch(row, filter, group) {
  if (!filter || filter === "all") return true;
  if (filter === "action") return group === "action";
  if (filter === "nemt") return !!row && row.provider_type === "nemt";
  if (filter === "care_team") return !!row && isCareTeamSource(row.provider_type);
  return true;
}

// Order on load: Needs action, then On track, then Done; the board's own order
// (pickup) inside each. Each row carries _group so a save on the card does not
// make it jump to another list until the next load.
export function sortBoard(rows, dayYmd, todayYmd, tomorrowYmd, nowMs = Date.now()) {
  const rank = { action: 0, track: 1, done: 2 };
  return (rows || []).map((r, i) => ({ r: { ...r, _group: boardGroup(r, dayYmd, todayYmd, tomorrowYmd, nowMs) }, i }))
    .sort((a, b) => (rank[a.r._group] - rank[b.r._group]) || (a.i - b.i))
    .map((x) => x.r);
}

// "Follow-up 10:00 AM - NEMT - ModivCare - Trip T-9 - Return: will call"
export function boardDetail(row) {
  if (!row) return "";
  const who = row.missing ? null : (row.provider_type === "nemt" ? "NEMT" + (row.plan_label ? " (" + row.plan_label + ")" : "") : sourceShort(row.provider_type) + (row.driver_name ? " (" + row.driver_name + ")" : ""));
  return [(row.appt_type || "Visit") + (row.start_at ? " " + pickupTime(row.start_at) : ""), who, row.vendor || null,
    row.trip_ref ? "Trip " + row.trip_ref : null, row.pickup_window_end ? windowLabel(row.pickup_at, row.pickup_window_end) : null,
    row.missing ? null : returnLabel(row), row.return_trip_ref ? "Return trip " + row.return_trip_ref : null,
    row.standing ? "Standing order" : null, row.combined_with ? "Same trip as the earlier visit" : null].filter(Boolean).join(" \u00b7 ");
}

// ---- Driver alerts (20270105100000_ride_driver_alerts) ----------------------
// A care manager's Care Schedule shows CHW visits for their caseload AND the
// visits they are on themselves (a ride they drive for any patient).
// PostgREST or() filter; ids are checked so nothing odd reaches the query.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export function cmVisitOrFilter(caseloadIds, userId) {
  const ids = (caseloadIds || []).filter((x) => UUID_RE.test(String(x)));
  const mine = UUID_RE.test(String(userId || "")) ? "scheduled_for_user_id.eq." + userId : null;
  const parts = [mine, ids.length ? "patient_id.in.(" + ids.join(",") + ")" : null].filter(Boolean);
  return parts.length ? parts.join(",") : null;
}

// "Leave by 8:40 AM - 22 min drive with traffic from where you are" for the
// driver's next ride (ride_driver_status().next).
export function leaveByLine(next) {
  if (!next || !next.leave_by_at) return null;
  const from = { live: "from where you are", previous_stop: "from your previous stop", office: "from the office" }[next.route_source];
  return "Leave by " + pickupTime(next.leave_by_at)
    + (next.drive_minutes != null ? " - " + next.drive_minutes + " min drive" + (next.route_source === "estimate" || !from ? " (estimate)" : " with traffic " + from) : "");
}

// ---- Directions for the driver (20270106100000_ride_driver_directions) ------
// Opens the phone's maps app with driving directions to an address. iPhone and
// iPad get Apple Maps; everything else gets Google Maps (the app when it is
// installed). The address goes only to the driver's own maps app.
export function mapsUrl(address, ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "") {
  const a = String(address || "").trim();
  if (!a) return null;
  const q = encodeURIComponent(a);
  return /iPhone|iPad|iPod/.test(ua) ? "https://maps.apple.com/?daddr=" + q + "&dirflg=d"
    : "https://www.google.com/maps/dir/?api=1&travelmode=driving&destination=" + q;
}

// Live updates (20270114100000_ride_realtime). Which postgres_changes feeds a
// ride screen listens to. Staff: the practice's rides, visits and care team
// visits. Driver: their own alerts. Patient (portal): their own rides and
// visits. RLS still applies to every feed; a change only triggers a refetch.
export const RIDE_LIVE_STAFF_TABLES = ["appointment_rides", "appointments", "chw_scheduled_visits"];
export function rideLiveSubs({ practiceId = null, patientId = null, driverId = null, tables = null } = {}) {
  const want = (t) => !tables || tables.includes(t);
  const out = [];
  if (practiceId) {
    for (const t of RIDE_LIVE_STAFF_TABLES) if (want(t)) out.push({ table: t, filter: "practice_id=eq." + practiceId });
  }
  if (driverId && want("ride_driver_events")) out.push({ table: "ride_driver_events", filter: "driver_id=eq." + driverId });
  if (patientId) {
    for (const t of ["appointment_rides", "appointments"]) if (want(t)) out.push({ table: t, filter: "patient_id=eq." + patientId });
  }
  return out;
}

// Rides block 4: the plan's ride line needs notice (ride_notice). Booking
// warns when the visit is too soon; saving still works.
export function shortNoticeText(n) {
  if (!n || !n.short) return null;
  let by = "";
  if (n.need_by && /^\d{4}-\d{2}-\d{2}$/.test(n.need_by)) {
    const [y, m, d] = n.need_by.split("-").map(Number);
    by = " (call by " + new Date(y, m - 1, d).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) + ")";
  }
  return "Short notice: " + (n.plan_label || "The plan") + " asks for " + (n.notice_days || 2) + " business days" + by
    + ". Call the ride line anyway for an urgent trip, or pick a care team or family ride.";
}

// ---- Rides block 6 (20270119100000_ride_block6) ----------------------------
// "On my way since 8:40 AM - the patient was texted (about 9:05 AM)".
export function enRouteLine(r) {
  if (!r || !r.en_route_at) return null;
  return "On my way since " + pickupTime(r.en_route_at) + " - the patient gets a text" + (r.en_route_eta ? " (arriving about " + pickupTime(r.en_route_eta) + ")" : "");
}
// The patient's answer to the day-before ride text, for the board.
export function patientReadyLabel(r) {
  if (!r || !r.patient_ready) return null;
  return r.patient_ready === "yes" ? "Patient texted YES: ready" : "Patient texted NO: call them";
}
// A pickup typed for this visit only (portal), for the board and the task.
export function onePickupLine(r) {
  const a = r && String(r.ride_pickup_address || r.pickup_address || "").trim();
  return a ? "Pickup for this visit: " + a : null;
}

// ---- Rides block 7 (20270120100000_ride_block7) ----------------------------
// "Standing order: ModivCare SO-1, through Nov 30" (null when none or ended).
export function standingLine(p, todayYmd = null) {
  if (!p || !p.standing_vendor || !p.standing_until) return null;
  // Fallback is the ride (practice) zone day, not the UTC day.
  const today = todayYmd || rideYmd(0);
  if (String(p.standing_until) < today) return null;
  const [y, m, d] = String(p.standing_until).split("-").map(Number);
  const until = new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  return "Standing order: " + p.standing_vendor + (p.standing_ref ? " " + p.standing_ref : "") + ", through " + until;
}
// The board's Combine offer: a later visit's open Medicaid ride with an earlier one the same day.
export function canCombine(row) {
  return !!row && !!row.same_day_ride_id && row.provider_type === "nemt" && !row.combined_with && !row.picked_up_at
    && (row.status === "requested" || row.status === "booked");
}
// "3 rides cancelled. 5 visits are still on the schedule that day: reschedule them."
export function closeDayNote(res) {
  const n = Number((res && res.rides_cancelled) || 0);
  const v = Number((res && res.visits_left) || 0);
  return (n === 1 ? "1 ride cancelled" : n + " rides cancelled") + " (vendor trips get a cancel task, drivers and patients are told)."
    + (v > 0 ? " " + v + " visit" + (v === 1 ? " is" : "s are") + " still on the schedule that day: reschedule " + (v === 1 ? "it." : "them.") : "");
}

// ---- Rides block 8 (20270121100000_ride_block8) ----------------------------
export const INCIDENT_TYPES = [
  { value: "fall", label: "Fall" }, { value: "injury", label: "Injury" }, { value: "behavior", label: "Behavior" },
  { value: "vehicle_accident", label: "Vehicle accident" }, { value: "left_behind", label: "Patient left behind" },
  { value: "late_drop", label: "Dropped off late" }, { value: "other", label: "Other" },
];
export const COMPLAINT_TYPES = [
  { value: "late", label: "Late" }, { value: "no_show", label: "Did not come" }, { value: "rude", label: "Rude driver" },
  { value: "unsafe", label: "Unsafe driving" }, { value: "wrong_vehicle", label: "Wrong vehicle" }, { value: "other", label: "Other" },
];
export const CALL_OUTCOMES = [
  { value: "no_answer", label: "No answer" }, { value: "on_hold", label: "On hold, call back" }, { value: "refused", label: "Refused the trip" },
];
// "No answer 9:40 AM (Ana) - On hold, call back 10:05 AM (Ana)"
export function callLogLine(records) {
  const calls = (records || []).filter((r) => r.kind === "vendor_call").slice().reverse();
  if (!calls.length) return null;
  return calls.map((c) => c.label + " " + pickupTime(c.created_at) + (c.created_by_name ? " (" + String(c.created_by_name).split(" ")[0] + ")" : "")).join(" - ");
}
// Reports: "Accepts in 18 min on average - 2 needed a follow-up"
export function acceptLine(s) {
  if (!s) return "";
  const parts = [s.accepted + " of " + s.asked + " accepted"];
  if (s.avg_accept_minutes != null) parts.push("in " + s.avg_accept_minutes + " min on average");
  if (s.followups) parts.push(s.followups + " needed the office to follow up");
  if (s.pending) parts.push(s.pending + " waiting now");
  return parts.join(" - ");
}
// The board's week strip: the Monday on or before a day.
export function weekStartYmd(ymd) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(ymd || ""))) return null;
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const back = (dt.getUTCDay() + 6) % 7;
  dt.setUTCDate(dt.getUTCDate() - back);
  return dt.toISOString().slice(0, 10);
}
// Care team rides grouped by driver (the supervisor view on the board).
export function groupByDriver(rows) {
  const out = new Map();
  for (const r of rows || []) {
    if (!(r.provider_type === "chw" || r.provider_type === "care_manager")) continue;
    const key = r.driver_id || "none";
    if (!out.has(key)) out.set(key, { driver_id: r.driver_id || null, name: r.driver_name || "No driver yet", rows: [], pending: 0, under_way: 0 });
    const g = out.get(key);
    g.rows.push(r);
    if (r.driver_ack === "pending") g.pending += 1;
    if (r.picked_up_at && !r.arrived_at) g.under_way += 1;
  }
  return Array.from(out.values()).sort((a, b) => b.rows.length - a.rows.length || a.name.localeCompare(b.name));
}
export const MILES_STATUS_LABEL = { approved: "Approved", paid: "Paid" };
